import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { generateFilterOptions } from 'utils';
import { Autocomplete } from 'components/FormFields';
import { FETCH_STATE } from 'constants';
import { AccessDeviceActions } from 'state/modules/accessDevice';

export const NFCCardSelect = ({
  field: { name, value },
  form: { errors, touched, setFieldValue },
  id,
  customNoOptionText,
  required = true,
  disabled = false,
  style = {},
  customParams = {},
  label = 'Select...',
  variant = 'standard',
  datacy = 'nfc-card-select',
  props,
}) => {
  const dispatch = useDispatch();
  const [params] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const filterOptions = generateFilterOptions('start', 'label');

  const options = useSelector((state) => state.accessDevice?.accessDevices);
  const fetchingAccessDevice = useSelector((state) => state.accessDevice?.fetchingAccessDevice);

  const loading = fetchingAccessDevice === FETCH_STATE.LOADING;

  const fetchAccessDevices = useCallback(
    (params = {}) => {
      dispatch(AccessDeviceActions.fetchAccessDevices(params));
    },
    [dispatch]
  );

  // Run once on mount, no debounce
  useEffect(() => {
    fetchAccessDevices({ ...params });
  }, []);

  const getOptionsLabel = (accessDevice) =>
    accessDevice?.id ? `${accessDevice?.device_id} | ${accessDevice?.name}` : '';
  const getOptionsSelected = (option, value) => option?.id === value?.id;
  const handleOnChange = (_, val) => setFieldValue(name, val?.id, val || '');

  const innerValue = useMemo(
    () => options.find((accessDevice) => accessDevice?.id == value) || '',
    [options, value]
  );

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      margin="none"
      dataCy={datacy}
      fullwidth="true"
      variant={variant}
      loading={loading}
      options={options}
      clearOnBlur={false}
      id={id}
      disabled={disabled}
      customNoOptionText={customNoOptionText}
      value={innerValue}
      filterOptions={filterOptions}
      getOptionLabel={getOptionsLabel}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={getOptionsSelected}
      onChange={handleOnChange}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      required={required}
      {...props}
    />
  );
};

NFCCardSelect.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
  id: PropTypes.number,
  customNoOptionText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  customParams: PropTypes.object,
  label: PropTypes.string,
  variant: PropTypes.string,
  datacy: PropTypes.string,
  props: PropTypes.object,
};
